import React, { useState, useEffect } from 'react';
import { database } from '../firebase';
import { ref, onValue, set, remove, push } from 'firebase/database';
import { useAuth } from '../hooks/useAuth';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const INSTITUTIONS = [
    { id: 'SEMAR', label: '⚓ Armada de México (SEMAR)', color: '#00f2fe' },
    { id: 'GUARDIA_NACIONAL', label: '🛡️ Guardia Nacional', color: '#4ade80' },
    { id: 'SEDENA', label: '⭐ SEDENA (Ejército / FAM)', color: '#f59e0b' },
];

const CATEGORIES = [
    { id: 'leyes', label: '📜 Leyes y Reglamentos', icon: '📜' },
    { id: 'cultura', label: '🧭 Cultura Institucional', icon: '🧭' },
    { id: 'tecnicos', label: '🔧 Conocimientos Técnicos', icon: '🔧' },
];

const getRTDBPrefix = (inst) => {
    return (!inst || inst === 'SEMAR') ? '' : `content/${inst.toLowerCase()}`;
};

const getMaterialPath = (inst, category) => {
    const prefix = getRTDBPrefix(inst);
    const basePath = prefix ? `${prefix}/material_estudio` : 'material_estudio';
    return `${basePath}/${category}`;
};

// ─── Empty form ───────────────────────────────────────────────────────────────

const EMPTY_FORM = {
    titulo: '',
    descripcion: '',
    contenido: '',
    url: '',
    jerarquia: 'TODOS',
    orden: 0,
    activo: true,
};

// ─── AdminStudyMaterial ───────────────────────────────────────────────────────

const AdminStudyMaterial = () => {
    const { user } = useAuth();

    const [selectedInst, setSelectedInst] = useState('SEMAR');
    const [selectedCat, setSelectedCat] = useState('leyes');
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState(EMPTY_FORM);
    const [editingKey, setEditingKey] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [saving, setSaving] = useState(false);
    const [expandedContent, setExpandedContent] = useState({});
    const [toast, setToast] = useState(null);

    const showToast = (msg, type = 'success') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 3500);
    };

    // ── Load items on inst/cat change ─────────────────────────────────────────
    useEffect(() => {
        setLoading(true);
        setItems([]);
        const path = getMaterialPath(selectedInst, selectedCat);
        console.log(`[AdminStudyMaterial] 📂 Leyendo: ${path}`);

        const dbRef = ref(database, path);
        const unsub = onValue(dbRef, (snap) => {
            if (snap.exists()) {
                const raw = snap.val();
                const list = Object.entries(raw).map(([key, val]) => ({ _key: key, ...val }));
                list.sort((a, b) => (a.orden || 0) - (b.orden || 0));
                setItems(list);
            } else {
                setItems([]);
            }
            setLoading(false);
        }, (err) => {
            console.error('[AdminStudyMaterial] Error:', err);
            setLoading(false);
        });
        return () => unsub();
    }, [selectedInst, selectedCat]);

    // ── Save (create or update) ───────────────────────────────────────────────
    const handleSave = async () => {
        if (!formData.titulo.trim()) return showToast('El título es obligatorio.', 'error');
        setSaving(true);
        try {
            const path = getMaterialPath(selectedInst, selectedCat);
            const payload = {
                titulo: formData.titulo.trim(),
                descripcion: formData.descripcion.trim(),
                contenido: formData.contenido.trim(),
                url: formData.url.trim(),
                jerarquia: formData.jerarquia,
                orden: Number(formData.orden) || 0,
                activo: formData.activo,
                updatedAt: Date.now(),
            };

            if (editingKey) {
                await set(ref(database, `${path}/${editingKey}`), { ...payload, key: editingKey });
                showToast('✅ Documento actualizado.');
            } else {
                const newRef = push(ref(database, path));
                await set(newRef, { ...payload, key: newRef.key, createdAt: Date.now() });
                showToast('✅ Documento agregado.');
            }
            setShowForm(false);
            setFormData(EMPTY_FORM);
            setEditingKey(null);
        } catch (err) {
            console.error('[AdminStudyMaterial] Save error:', err);
            showToast('❌ Error al guardar: ' + err.message, 'error');
        } finally {
            setSaving(false);
        }
    };

    // ── Delete ────────────────────────────────────────────────────────────────
    const handleDelete = async (key) => {
        if (!window.confirm('¿Eliminar este documento? Esta acción es irreversible.')) return;
        try {
            const path = getMaterialPath(selectedInst, selectedCat);
            await remove(ref(database, `${path}/${key}`));
            showToast('🗑️ Documento eliminado.');
        } catch (err) {
            showToast('❌ Error al eliminar: ' + err.message, 'error');
        }
    };

    // ── Edit ──────────────────────────────────────────────────────────────────
    const handleEdit = (item) => {
        setFormData({
            titulo: item.titulo || '',
            descripcion: item.descripcion || '',
            contenido: item.contenido || '',
            url: item.url || '',
            jerarquia: item.jerarquia || 'TODOS',
            orden: item.orden || 0,
            activo: item.activo !== false,
        });
        setEditingKey(item._key);
        setShowForm(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const instColor = INSTITUTIONS.find(i => i.id === selectedInst)?.color || '#00f2fe';

    // ─── Styles (inline) ─────────────────────────────────────────────────────
    const card = {
        background: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: '16px',
        padding: '20px 24px',
        marginBottom: '12px',
    };

    return (
        <div style={{ padding: '24px', color: 'var(--text-primary)', maxWidth: '1100px', margin: '0 auto' }}>

            {/* ── TOAST ── */}
            {toast && (
                <div style={{
                    position: 'fixed', top: '20px', right: '20px', zIndex: 9999,
                    background: toast.type === 'error' ? 'rgba(239,68,68,0.15)' : 'rgba(74,222,128,0.15)',
                    border: `1px solid ${toast.type === 'error' ? '#ef4444' : '#4ade80'}`,
                    color: toast.type === 'error' ? '#ef4444' : '#4ade80',
                    padding: '12px 20px', borderRadius: '12px',
                    backdropFilter: 'blur(12px)', fontWeight: '600', fontSize: '0.9rem',
                    boxShadow: '0 4px 24px rgba(0,0,0,0.4)',
                }}>
                    {toast.msg}
                </div>
            )}

            {/* ── HEADER ── */}
            <div style={{ marginBottom: '28px' }}>
                <h1 style={{ margin: 0, fontSize: '2rem', fontWeight: '800', color: instColor }}>
                    📚 Material de Estudio
                </h1>
                <p style={{ margin: '6px 0 0', opacity: 0.55, fontSize: '0.9rem' }}>
                    Administra el material de estudio por institución y categoría
                </p>
            </div>

            {/* ── INSTITUTION TABS ── */}
            <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', flexWrap: 'wrap' }}>
                {INSTITUTIONS.map(inst => (
                    <button
                        key={inst.id}
                                                    onClick={() => { setSelectedInst(inst.id); setShowForm(false); setEditingKey(null); setFormData(EMPTY_FORM); setExpandedContent({}); }}
                        style={{
                            padding: '10px 20px', borderRadius: '50px', fontWeight: '700',
                            cursor: 'pointer', fontSize: '0.85rem', transition: 'all 0.2s',
                            background: selectedInst === inst.id ? inst.color : 'rgba(255,255,255,0.05)',
                            border: `1px solid ${selectedInst === inst.id ? inst.color : 'rgba(255,255,255,0.1)'}`,
                            color: selectedInst === inst.id ? '#0a0e17' : 'rgba(255,255,255,0.7)',
                            boxShadow: selectedInst === inst.id ? `0 0 20px ${inst.color}40` : 'none',
                        }}
                    >
                        {inst.label}
                    </button>
                ))}
            </div>

            {/* ── CATEGORY TABS ── */}
            <div style={{ display: 'flex', gap: '10px', marginBottom: '24px', flexWrap: 'wrap' }}>
                {CATEGORIES.map(cat => (
                    <button
                        key={cat.id}
                                                    onClick={() => { setSelectedCat(cat.id); setShowForm(false); setEditingKey(null); setFormData(EMPTY_FORM); setExpandedContent({}); }}
                        style={{
                            padding: '8px 18px', borderRadius: '10px', fontWeight: '600',
                            cursor: 'pointer', fontSize: '0.82rem', transition: 'all 0.2s',
                            background: selectedCat === cat.id ? `${instColor}20` : 'rgba(255,255,255,0.04)',
                            border: `1px solid ${selectedCat === cat.id ? instColor : 'rgba(255,255,255,0.08)'}`,
                            color: selectedCat === cat.id ? instColor : 'rgba(255,255,255,0.55)',
                        }}
                    >
                        {cat.label}
                    </button>
                ))}
            </div>

            {/* ── FORM ── */}
            {showForm && (
                <div style={{ ...card, border: `1px solid ${instColor}50`, marginBottom: '24px', background: `${instColor}08` }}>
                    <h3 style={{ margin: '0 0 16px', color: instColor }}>
                        {editingKey ? '✏️ Editar Documento' : '➕ Nuevo Documento'}
                    </h3>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                        <div style={{ gridColumn: '1 / -1' }}>
                            <label style={lblStyle}>Título *</label>
                            <input
                                style={inputStyle}
                                placeholder="Ej: Ley de la Guardia Nacional"
                                value={formData.titulo}
                                onChange={e => setFormData(p => ({ ...p, titulo: e.target.value }))}
                            />
                        </div>
                        <div style={{ gridColumn: '1 / -1' }}>
                            <label style={lblStyle}>Descripción</label>
                            <textarea
                                 style={{  ...inputStyle,  height: '80px',  resize: 'vertical'  }} className="adm-input"
                                placeholder="Breve descripción del documento..."
                                value={formData.descripcion}
                                onChange={e => setFormData(p => ({ ...p, descripcion: e.target.value }))}
                            />
                        </div>
                        <div style={{ gridColumn: '1 / -1' }}>
                            <label style={lblStyle}>URL del Documento (Dropbox, Drive, etc.) — opcional si usas contenido inline</label>
                            <input
                                style={inputStyle}
                                placeholder="https://..."
                                value={formData.url}
                                onChange={e => setFormData(p => ({ ...p, url: e.target.value }))}
                            />
                        </div>
                        <div style={{ gridColumn: '1 / -1' }}>
                            <label style={lblStyle}>Contenido del Capítulo (texto completo)</label>
                            <textarea
                                 style={{  ...inputStyle,  height: '300px',  resize: 'vertical',  fontFamily: 'monospace',  textAlign: 'justify'  }} className="adm-input"
                                placeholder="Pega aqui todo el contenido del capitulo..."
                                value={formData.contenido}
                                onChange={e => setFormData(p => ({ ...p, contenido: e.target.value }))}
                            />
                            <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.35)', marginTop: '4px', textAlign: 'right' }}>
                                {formData.contenido.length.toLocaleString()} caracteres
                            </div>
                        </div>
                        <div>
                            <label style={lblStyle}>Jerarquía</label>
                            <input
                                style={inputStyle}
                                placeholder="TODOS / MRO / SGTO_2_O / etc."
                                value={formData.jerarquia}
                                onChange={e => setFormData(p => ({ ...p, jerarquia: e.target.value }))}
                            />
                        </div>
                        <div>
                            <label style={lblStyle}>Orden</label>
                            <input
                                type="number"
                                style={inputStyle}
                                placeholder="0"
                                value={formData.orden}
                                onChange={e => setFormData(p => ({ ...p, orden: e.target.value }))}
                            />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <input
                                type="checkbox"
                                id="activo-check"
                                checked={formData.activo}
                                onChange={e => setFormData(p => ({ ...p, activo: e.target.checked }))}
                            />
                            <label htmlFor="activo-check"  className="adm-label">
                                Visible para usuarios
                            </label>
                        </div>
                    </div>
                    <div style={{ display: 'flex', gap: '10px', marginTop: '18px' }}>
                        <button
                            onClick={handleSave}
                            disabled={saving}
                             className="adm-btn adm-btn--secondary"
                        >
                            {saving ? 'Guardando...' : (editingKey ? 'Actualizar' : 'Guardar')}
                        </button>
                        <button
                            onClick={() => { setShowForm(false); setEditingKey(null); setFormData(EMPTY_FORM); }}
                            style={{
                                padding: '10px 20px', borderRadius: '10px', fontWeight: '600',
                                background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
                                color: 'rgba(255,255,255,0.6)', cursor: 'pointer',
                            }}
                        >
                            Cancelar
                        </button>
                    </div>
                </div>
            )}

            {/* ── ADD BUTTON ── */}
            {!showForm && (
                <button
                    onClick={() => { setShowForm(true); setEditingKey(null); setFormData(EMPTY_FORM); }}
                    style={{
                        display: 'flex', alignItems: 'center', gap: '8px',
                        padding: '10px 24px', borderRadius: '50px', fontWeight: '700',
                        background: `${instColor}20`, border: `1px solid ${instColor}60`,
                        color: instColor, cursor: 'pointer', marginBottom: '20px',
                        fontSize: '0.9rem', transition: 'all 0.2s',
                    }}
                >
                    ➕ Agregar Documento
                </button>
            )}

            {/* ── RTDB PATH INDICATOR ── */}
            <div style={{
                display: 'inline-flex', alignItems: 'center', gap: '8px',
                background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)',
                borderRadius: '8px', padding: '6px 14px', marginBottom: '20px',
                fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)', fontFamily: 'monospace',
            }}>
                📍 RTDB: <strong style={{ color: 'rgba(255,255,255,0.65)' }}>/{getMaterialPath(selectedInst, selectedCat)}</strong>
            </div>

            {/* ── ITEM LIST ── */}
            {loading ? (
                <div style={{ textAlign: 'center', padding: '40px', opacity: 0.5 }}>
                    Cargando documentos...
                </div>
            ) : items.length === 0 ? (
                <div style={{
                    ...card, textAlign: 'center', padding: '50px',
                    color: 'rgba(255,255,255,0.3)', fontSize: '0.9rem',
                }}>
                    <div style={{ fontSize: '3rem', marginBottom: '12px' }}>📭</div>
                    No hay documentos en esta categoría para{' '}
                    <strong>{INSTITUTIONS.find(i => i.id === selectedInst)?.label}</strong>.
                    <br />Usa el botón de arriba para agregar el primero.
                </div>
            ) : (
                <div>
                    {items.map(item => (
                        <div
                            key={item._key}
                            style={{
                                ...card,
                                borderLeft: `3px solid ${item.activo !== false ? instColor : 'rgba(255,255,255,0.1)'}`,
                                display: 'flex', alignItems: 'flex-start',
                                gap: '16px', opacity: item.activo === false ? 0.45 : 1,
                                transition: 'all 0.2s',
                            }}
                        >
                            <div style={{
                                width: '40px', height: '40px', borderRadius: '10px',
                                background: `${instColor}15`, display: 'flex',
                                alignItems: 'center', justifyContent: 'center',
                                fontSize: '1.3rem', flexShrink: 0,
                            }}>
                                {CATEGORIES.find(c => c.id === selectedCat)?.icon || '📄'}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                                    <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: '700' }}>{item.titulo}</h4>
                                    <span style={{
                                        fontSize: '0.7rem', padding: '2px 8px', borderRadius: '20px',
                                        background: `${instColor}20`, color: instColor, fontWeight: '600',
                                    }}>{item.jerarquia || 'TODOS'}</span>
                                    {item.activo === false && (
                                        <span style={{
                                            fontSize: '0.7rem', padding: '2px 8px', borderRadius: '20px',
                                            background: 'rgba(239,68,68,0.15)', color: '#ef4444', fontWeight: '600',
                                        }}>OCULTO</span>
                                    )}
                                    <span style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.3)' }}>#{item.orden}</span>
                                </div>
                                {item.descripcion && (
                                    <p style={{ margin: '4px 0 0', fontSize: '0.82rem', opacity: 0.55, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {item.descripcion}
                                    </p>
                                )}
                                {item.url && (
                                    <a
                                        href={item.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ fontSize: '0.75rem', color: instColor, textDecoration: 'none', opacity: 0.8, display: 'block', marginTop: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                    >
                                        🔗 {item.url}
                                    </a>
                                )}
                                {item.contenido && (
                                    <div style={{ marginTop: '8px' }}>
                                        <button
                                            onClick={() => setExpandedContent(p => ({ ...p, [item._key]: !p[item._key] }))}
                                            style={{
                                                background: 'none', border: 'none', color: instColor, cursor: 'pointer',
                                                fontSize: '0.78rem', fontWeight: '600', padding: '4px 0',
                                                display: 'flex', alignItems: 'center', gap: '6px',
                                            }}
                                        >
                                            {expandedContent[item._key] ? '📕 Ocultar contenido' : '📖 Ver contenido'}
                                            <span style={{ fontSize: '0.7rem', opacity: 0.5 }}>
                                                ({item.contenido.length.toLocaleString()} chars)
                                            </span>
                                        </button>
                                        {expandedContent[item._key] && (
                                            <div
                                                style={{
                                                    marginTop: '10px', padding: '16px 20px',
                                                    background: 'rgba(0,0,0,0.3)', borderRadius: '10px',
                                                    border: '1px solid rgba(255,255,255,0.06)',
                                                    fontSize: '0.82rem', lineHeight: '1.7',
                                                    textAlign: 'justify', color: 'rgba(255,255,255,0.8)',
                                                    whiteSpace: 'pre-wrap', fontFamily: 'monospace',
                                                    maxHeight: '500px', overflowY: 'auto',
                                                }}
                                            >
                                                {item.contenido}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                            <div style={{ display: 'flex', gap: '8px', flexShrink: 0, alignItems: 'flex-start' }}>
                                <button
                                    onClick={() => handleEdit(item)}
                                    style={actionBtn('#3b82f6')}
                                >
                                    ✏️
                                </button>
                                <button
                                    onClick={() => handleDelete(item._key)}
                                    style={actionBtn('#ef4444')}
                                >
                                    🗑️
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

// ── Micro-styles ───────────────────────────────────────────────────────────────

const inputStyle = {
    width: '100%',
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '10px',
    padding: '10px 14px',
    color: 'white',
    fontSize: '0.88rem',
    outline: 'none',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
};

const lblStyle = {
    display: 'block',
    marginBottom: '6px',
    fontSize: '0.78rem',
    color: 'rgba(255,255,255,0.55)',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
};

const actionBtn = (color) => ({
    width: '34px', height: '34px', borderRadius: '8px',
    background: `${color}15`, border: `1px solid ${color}40`,
    color: color, cursor: 'pointer', fontSize: '1rem',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    transition: 'all 0.2s',
});

export default AdminStudyMaterial;
